// Copyright (c) 2025 Signal from Noise, Inc. All rights reserved.

import {
  type Action,
  type ActionsSync,
  ActionsSyncRequestConfig,
  type CreateActionInput,
  DeleteActionRequestConfig,
  DryRunActionRequestConfig,
  type ExecutionMetas,
  type ExecutionsQueryParams,
  GetActionRequestConfig,
  ObtainExecutionsRequestConfig,
  TransferActionRequestConfig,
  type UpdateActionInput,
  UpdateActionRequestConfig,
  useStorytellClient,
} from "@storytell/client";
import { err, ok, type Result } from "neverthrow";
import { createSignal } from "solid-js";

interface ActionsServiceConfig {
  authReady: () => boolean;
  authToken: () => string | undefined;
}

export interface DryRunOptions {
  emails?: string[];
  /** Optional provenance target for page-driven actions (a Notion page). */
  source?: string;
  sourceRef?: string;
  sourceUrl?: string;
}

export interface ActionsService {
  /** All synced actions as a reactive signal */
  actions: () => Action[];
  /** Whether the service is currently loading */
  isLoading: () => boolean;
  /** Sync all actions for the current context */
  sync: () => Promise<Result<ActionsSync, Error>>;
  /** Get a single action by ID */
  get: (id: string) => Promise<Result<Action, Error>>;
  /** Get executions (run history) with optional filters */
  getExecutions: (params?: ExecutionsQueryParams) => Promise<Result<ExecutionMetas, Error>>;
  /** Create a new action */
  create: (input: CreateActionInput) => Promise<Result<Action, Error>>;
  /** Update an existing action */
  update: (id: string, input: UpdateActionInput) => Promise<Result<Action, Error>>;
  /** Transfer ownership of an action to the calling user */
  transfer: (id: string, userId?: string) => Promise<Result<Action, Error>>;
  /** Delete an action */
  delete: (id: string) => Promise<Result<void, Error>>;
  /** Execute a dry run of an action */
  dryRun: (id: string, options?: DryRunOptions) => Promise<Result<{ id: string }, Error>>;
  /** Find an action by ID from the cached list */
  findById: (id: string) => Action | undefined;
}

export function createActionsService(config: ActionsServiceConfig): ActionsService {
  const client = useStorytellClient();
  const [actions, setActions] = createSignal<Action[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);

  const getToken = (): string | undefined => {
    if (!config.authReady()) return undefined;
    return config.authToken();
  };

  const sync = async (): Promise<Result<ActionsSync, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    setIsLoading(true);
    try {
      const request = ActionsSyncRequestConfig.withBearerToken(token);
      const result = await client.fetch(request, undefined, undefined);

      if (result.isErr()) {
        return err(result.error);
      }

      // Convert record to array and update signal
      const actionsArray = Object.values(result.value.actions);
      setActions(actionsArray);

      return ok(result.value);
    } finally {
      setIsLoading(false);
    }
  };

  const get = async (id: string): Promise<Result<Action, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const request = GetActionRequestConfig.withBearerToken(token);
    const result = await client.fetch(request, { id }, undefined);

    // Update the cache with the fetched action
    if (result.isOk()) {
      setActions((prev) => {
        const exists = prev.some((a) => a.id === id);
        if (exists) {
          return prev.map((a) => (a.id === id ? result.value : a));
        }
        return [...prev, result.value];
      });
    }

    return result;
  };

  const getExecutions = async (params?: ExecutionsQueryParams): Promise<Result<ExecutionMetas, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const request = ObtainExecutionsRequestConfig.withBearerToken(token);
    return client.fetch(request, params ?? {}, undefined);
  };

  const create = async (input: CreateActionInput): Promise<Result<Action, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const { CreateActionRequestConfig } = await import("@storytell/client");
    const request = CreateActionRequestConfig.withBearerToken(token);
    const result = await client.fetch(request, undefined, input);

    if (result.isOk()) {
      // Add the new action to the list
      setActions((prev) => [...prev, result.value]);
    }

    return result;
  };

  const update = async (id: string, input: UpdateActionInput): Promise<Result<Action, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const request = UpdateActionRequestConfig.withBearerToken(token);
    const result = await client.fetch(request, { id }, input);

    if (result.isOk()) {
      // Update the action in the list
      setActions((prev) => prev.map((a) => (a.id === id ? result.value : a)));
    }

    return result;
  };

  // An omitted userId claims the action for the caller, which is what the
  // orphaned-owner banner does; naming one hands it to that project member.
  const transfer = async (id: string, userId?: string): Promise<Result<Action, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const request = TransferActionRequestConfig.withBearerToken(token);
    const result = await client.fetch(request, { id }, { userId });

    if (result.isOk()) {
      setActions((prev) => prev.map((a) => (a.id === id ? result.value : a)));
    }

    return result;
  };

  const deleteAction = async (id: string): Promise<Result<void, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const request = DeleteActionRequestConfig.withBearerToken(token);
    const result = await client.fetch(request, { id }, undefined);

    if (result.isOk()) {
      // Remove the action from the list
      setActions((prev) => prev.filter((a) => a.id !== id));
    }

    return result.map(() => undefined);
  };

  const dryRun = async (id: string, options?: DryRunOptions): Promise<Result<{ id: string }, Error>> => {
    const token = getToken();
    if (!token) {
      return err(new Error("Not authenticated"));
    }

    const request = DryRunActionRequestConfig.withBearerToken(token);
    const result = await client.fetch(
      request,
      { id },
      {
        emails: options?.emails,
        source: options?.source,
        sourceRef: options?.sourceRef,
        sourceUrl: options?.sourceUrl,
      }
    );

    return result.map((chat) => ({ id: chat.id }));
  };

  const findById = (id: string): Action | undefined => {
    return actions().find((a) => a.id === id);
  };

  return {
    actions,
    isLoading,
    sync,
    get,
    getExecutions,
    create,
    update,
    transfer,
    delete: deleteAction,
    dryRun,
    findById,
  };
}
